/**
 * Панель стрімера на сайті (див. backend/src/plugins/stream-studio).
 *
 * Годує дві верстки: коротку панель у чаті ефіру (ChatStreamerPanel) і повний
 * док стрімера /studio-dock. Це той самий екран, що й «Панель стрімера» в
 * адмінці, і правила ті самі, з однією різницею: ключів трансляції тут немає
 * й бути не може. Сервер їх цьому входу не віддає: чат і док часто самі
 * потрапляють у кадр, а ключ, що мигнув на стрімі, — це вкрадений ефір.
 *
 * Пускає лише глядача з галочкою «Стрімер» у картці користувача (Strapi →
 * Users); решта отримає 403.
 */

// Лічильники мають виглядати живими, а рахує їх бекенд у памʼяті — запит
// дешевий. Опитуємо лише поки вкладка видима
const POLL = 3000

/**
 * @param {object} [options]
 * @param {number} [options.points] скільки точок графіка тягнути (0 — увесь ефір)
 */
export const useStreamStudio = ({ points = 60 } = {}) => {
  const config = useRuntimeConfig()
  const token = useStrapiToken()

  const state = ref(null)
  const error = ref('')
  // Код останньої відмови: 401 — не увійшли, 403 — не стрімер
  const status = ref(0)
  const busy = ref(false)
  // Коли прийшла відповідь — годинник ефіру йде від неї щосекунди
  const fetchedAt = ref(Date.now())
  const now = ref(Date.now())

  const base = () => `${config.public.apiBase || ''}/api/stream-studio`

  const call = async (path, options = {}) => {
    try {
      return await $fetch(`${base()}${path}`, {
        headers: { Authorization: `Bearer ${token.value}` },
        ...options
      })
    } catch (e) {
      status.value = e?.status || e?.response?.status || 0
      throw new Error(e?.data?.error?.message || 'Не вдалося — спробуйте ще раз')
    }
  }

  const load = async () => {
    if (!token.value) {
      status.value = 401
      error.value = 'Треба увійти'
      return
    }
    try {
      state.value = await call(`/state?points=${points}`)
      fetchedAt.value = Date.now()
      status.value = 200
      error.value = ''
    } catch (e) {
      error.value = e.message
    }
  }

  // Дія з перечитуванням стану: після неї панель має показати нове одразу
  const act = async (fn) => {
    busy.value = true
    try {
      const result = await fn()
      await load()
      return result ?? true
    } catch (e) {
      error.value = e.message
      return false
    } finally {
      busy.value = false
    }
  }

  const updateSettings = (body) => act(() => call('/settings', { method: 'PUT', body }))
  const applyPreset = (id) => act(() => call(`/presets/${id}/apply`, { method: 'POST' }))
  const saveCurrentAsPreset = (name) =>
    act(() => call('/presets/from-current', { method: 'POST', body: { name } }))
  const createPreset = (body) => act(() => call('/presets', { method: 'POST', body }))
  const updatePreset = (id, body) => act(() => call(`/presets/${id}`, { method: 'PUT', body }))
  const deletePreset = (id) => act(() => call(`/presets/${id}`, { method: 'DELETE' }))
  const reorderPresets = (ids) => act(() => call('/presets/order', { method: 'PUT', body: { ids } }))

  // Постер — у бібліотеку адмінки; повертає { id, url, thumb }
  const uploadPoster = async (file) => {
    const form = new FormData()
    form.append('file', file)
    const { poster } = await call('/poster', { method: 'POST', body: form })
    return poster
  }

  const sessions = (page = 1) => call(`/sessions?page=${page}&pageSize=15`)
  const session = (id) => call(`/sessions/${id}`)

  const uptime = computed(() => {
    const current = state.value?.session
    if (!current) return 0
    return current.uptimeSec + Math.floor((now.value - fetchedAt.value) / 1000)
  })

  if (import.meta.client) {
    let pollTimer = null
    let clockTimer = null

    const tick = () => {
      if (document.visibilityState === 'visible' && !busy.value) load()
    }

    onMounted(() => {
      load()
      pollTimer = setInterval(tick, POLL)
      clockTimer = setInterval(() => {
        now.value = Date.now()
      }, 1000)
    })

    onBeforeUnmount(() => {
      clearInterval(pollTimer)
      clearInterval(clockTimer)
    })
  }

  return {
    state,
    error,
    status,
    busy,
    uptime,
    load,
    updateSettings,
    applyPreset,
    saveCurrentAsPreset,
    createPreset,
    updatePreset,
    deletePreset,
    reorderPresets,
    uploadPoster,
    sessions,
    session
  }
}
